import { useState, useEffect, useRef, createElement, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Paper, Center, Text, Loader,Stack, Button, Title, Group, Container, Flex} from '@mantine/core';
import { IconArrowLeft,IconCircleDashedX } from '@tabler/icons-react';
import { Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';

import { setActiveStep, setStepId, SavePreRegistration,GetKnownUser,setCheckoutSummary } from '../actions/ResponsesActions';
import { CargarCarrerasIniciales } from '../actions/CarreraActions';
import { ResolverLinkEvento, ClearLinkEvento } from '../actions/LinkEventoActions';

import Preloader from '../components/Preloader';
import { notifyError } from '../components/Toast';
import BonusesInfoModal from '../components/BonusesInfoModal';
import SideStepper from '../components/SideStepper';
import TextPriceWithDiscount from '../components/TextPriceWithDiscount';
import {stepIds} from "../enums/fieldsDataConfig";
import { useStepResponses } from '../hooks/useStepResponses';
import useFormTracking from '../hooks/useFormTracking';
import { getStepsConfig } from '../config/stepsConfig';
import typeConfig from '../config/typeConfig';
import parseNumber from "../utils/parseNumber";
import useCheckoutParams from '../hooks/useCheckoutParams';


const CheckoutPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formRefs = useRef({});

  const {data: dataCarreras} = useSelector((state) => state.carreras.carreras);
  const {data: dataCampus} = useSelector((state) => state.carreras.campus);
  const {data: dataTurnos} = useSelector((state) => state.carreras.turnos);
  const valorNormal = useSelector((state) => state.carreras.valor.normal);
  const valorComunidad = useSelector((state) => state.carreras.valor.comunidad);
  const {activeStep,steps:stepResponses,bonus} = useSelector((state) => state.respuestas);
  const knownUser = useSelector((state) => state.respuestas.knownUser);
  const summary = useSelector((state) => state.respuestas.checkoutSummary);
  const linkEvento = useSelector((state) => state.respuestas.linkEvento) || {};

  const ProgramResponse = useStepResponses(stepIds.PROGRAMA);
  const ConfirmationResponse = useStepResponses(stepIds.CONFIRMACION);
  const [knownUserLoaded, setKnownUserLoaded] = useState(false);
  const [highestStepVisited, setHighestStepVisited] = useState(activeStep);
  const [bonusesOpened, setBonusesOpened] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  const [steps, setSteps] = useState([]);

  const { track, resetStepTimer } = useFormTracking(activeStep, { abandono: true });
  const isInitialStepRender = useRef(true);
  const sectionTitleRef = useRef(null);

  const [finishStatus, setFinishStatus] = useState({
    finishInit: false,
    isLoading: false,
    saveSuccess: null,
  });

  const queryParams = useCheckoutParams();

  const handleUrlParams = useCallback(() => {
    if (queryParams.token) {
      const timeout = () => setTimeout(() => {
        setKnownUserLoaded(true);
      }, 5000);

      dispatch(GetKnownUser(queryParams.token, queryParams.t, queryParams.e)).then(() => {
        timeout()
      });
    }

    // Carreras en la fase de carga, siempre (antes solo con ?c=&p=&fi=, y con fi crudo): el paso
    // de programa arranca con el combo lleno, con la misma fecha que el form muestra.
    const dispatchGetCarreras = () => {
      dispatch(CargarCarrerasIniciales(queryParams));
    };

    if (queryParams.l) {
      // Resolver el link ANTES de cargar carreras: así el auto-select de turno nunca dispara
      // getCoste con el tipo de asistente por defecto ("externo") antes de conocer el del link.
      // La action nunca rechaza (ante error deja linkEvento.error y devuelve null), y el checkout
      // continúa con el flujo normal.
      dispatch(ResolverLinkEvento(queryParams.l)).then(() => dispatchGetCarreras());
    } else {
      // Sin "l" en la URL: limpiar restos de una visita anterior (redux-persist en sessionStorage).
      dispatch(ClearLinkEvento());
      dispatchGetCarreras();
    }

  }, [queryParams, dispatch]);

  const stepsConfig = useMemo(() => getStepsConfig(queryParams, typeConfig), [queryParams, typeConfig]);

  const initializeSteps = () => {
    const steps = stepsConfig
      .filter(step => step.isEnable)
      .map((step, index) => {
        const stepNumber = index + 1;
        dispatch(setStepId(stepNumber, step.id));
        return {
          number: stepNumber,
          ...step,
          form: createElement(step.form, {
            key: stepNumber,
            ref: (el) => (formRefs.current[stepNumber] = el),
            stepNumber: stepNumber,
            queryParams: queryParams,
            id: step.id,
          }),
        };
      });
    setSteps(steps);
  };

  const initializeWindowSize = () => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 700);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }

  useEffect(() => {
    initializeSteps();
    initializeWindowSize();
    track('form_started');
  }, []);

  useEffect(() => {
    if (isInitialStepRender.current) {
      isInitialStepRender.current = false;
      return;
    }
    track('step_changed', { fieldName: `step_${activeStep + 1}` });
    resetStepTimer();
    if (sectionTitleRef.current) {
      const headerHeight = document.getElementById('app-top-navbar')?.offsetHeight || 0;
      const titleTop = sectionTitleRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: titleTop - headerHeight - 16, behavior: 'smooth' });
    }
  }, [activeStep]);

  // Regla unica de precios: los dos slots (normal / comunidad) se combinan aca y en
  // ningun otro lado. precioComunidad = null significa "no aplica o todavia no llego";
  // 0 ES un precio valido (hoy la bonificacion comunidad es del 100% -> matricula $0).
  function getPreciosYDescuentos() {
    const n = valorNormal.data?.[0];
    if (!n) {
      console.warn('No se encontraron valores de carrera para calcular precios y descuentos');
      return {
        precioOriginal: 0,
        precioDescuento: 0,
        precioComunidad: null,
        descuentoPorcentaje: 0,
        descuentoVencimiento: null,
        descuentoId: null,
      };
    }

    const parse = (v) => parseNumber(v);
    const c = knownUser.comunity ? valorComunidad.data?.[0] : null;

    const precioOriginal = parse(n.f_valor_matricula);
    const precioDescuento = parse(n.f_matric_con_desc);
    const precioComunidad = c != null ? parse(c.f_matric_con_desc) : null;

    // El slot que "gana" define %, vencimiento y descuentoId, para que lo que se
    // muestra, lo que se cobra y lo que se registra en SIGUE salgan del mismo lugar.
    const winning = c != null ? c : n;

    return {
      precioOriginal,
      precioDescuento,
      precioComunidad,
      descuentoPorcentaje: parse(winning.f_porc_bonif_matricula),
      descuentoVencimiento: winning.dt_bonif_matr_hasta || null,
      descuentoId: winning.c_descuento_id || null,
    };
  }

  useEffect(() => {
    if (
      !dataCarreras || !dataCampus || !dataTurnos || !valorNormal.data || valorNormal.data.length === 0 ||
      !ProgramResponse.carrera || !ProgramResponse.campus || !ProgramResponse.turno || !ProgramResponse.fechaIngreso
    ) return;

    const careerDetail = dataCarreras.find((f) => f.careers.find((c) => c.cod === ProgramResponse.carrera))?.careers.find((c) => c.cod === ProgramResponse.carrera)?.description;
    const campus =  dataCampus.find((c) => c.c_campus_id === ProgramResponse.campus)?.vc_descripcion;
    const turno = dataTurnos.find((t) => t.c_turno_id === ProgramResponse.turno)?.vc_descripcion;
    const fechaIngreso = ProgramResponse.fechaIngreso;

    const {
      precioOriginal,
      precioDescuento,
      precioComunidad,
      descuentoPorcentaje,
      descuentoVencimiento,
      descuentoId
    } = getPreciosYDescuentos();

    dispatch(setCheckoutSummary({
      carrera: careerDetail,
      fechaIngreso,
      campus,
      turno,
      precioOriginal,
      precioDescuento,
      precioComunidad,
      descuentoVencimiento,
      descuentoPorcentaje,
      descuentoId,
    }));

  }, [dataCarreras, dataCampus, dataTurnos, valorNormal.data, valorComunidad.data, knownUser]);


  useEffect(() => {
    handleUrlParams();
  }, [handleUrlParams]);

  const handleSavePreRegistration = async () => {
    track('form_submitted');
    setFinishStatus((prev) => ({...prev,finishInit: true, isLoading: true}));
    try {
      await dispatch(SavePreRegistration(stepResponses, queryParams.t, navigate, queryParams.e));
      track('form_completed');
    } catch (error) {
      track('form_submission_failed');
      setFinishStatus((prev) => ({...prev,saveSuccess: false}));

      setTimeout(() => {
        handleStepChange(activeStep)
        setFinishStatus((prev) => ({...prev,finishInit: false}));
      }, 8000); 
      
    } finally {
      setFinishStatus((prev) => ({...prev,isLoading: false}));
    }
  };

  const handleBonusesButtonClick = () => {
    setBonusesOpened(true);
  };

  const handleStepChange = async (nextStep) => {
    const isOutOfBounds = nextStep > steps.length || nextStep < 0;
    if (isOutOfBounds) return;

    // Nombra los campos que fallaron y lleva al usuario al primero. "Complete todos los campos
    // requeridos" no alcanzaba: los errores rojos suelen quedar fuera de pantalla, y un campo
    // cuyo input no se renderiza (ver provincia con país != AR) bloqueaba sin dejar rastro.
    // ponytail: depende de aria-invalid de Mantine; si cambia, cae al mensaje generico de antes.
    const avisoCamposInvalidos = async () => {
      // form.validate() actualiza los errores por estado: hay que esperar el repintado.
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const invalidos = [...document.querySelectorAll('[aria-invalid="true"]')];
      if (!invalidos.length) return 'Complete todos los campos requeridos';

      invalidos[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      const nombres = invalidos
        .map((el) => el.labels?.[0]?.textContent?.trim().replace(/\s*\*$/, ''))
        .filter(Boolean)
        .slice(0, 2);
      if (!nombres.length) return 'Complete todos los campos requeridos';

      const restantes = invalidos.length - nombres.length;
      return `Revisá: ${nombres.join(' y ')}${restantes > 0 ? ` (y ${restantes} campo${restantes > 1 ? 's' : ''} más)` : ''}`;
    };
  
    const isForward = nextStep > activeStep;
  
    if (isForward) {
      const currentFormRef = formRefs.current[activeStep + 1 ];

      const isValid = await currentFormRef.validate();
      if (isValid) {
        dispatch(setActiveStep(nextStep));
        setHighestStepVisited((hSC) => Math.max(hSC, nextStep));

        if(nextStep === steps.length){
          setFinishStatus((prev) => ({...prev,finishInit: true}));
          handleSavePreRegistration()
        }

      } else {
        track('step_validation_error', { fieldName: `step_${activeStep + 1}` });
        notifyError(await avisoCamposInvalidos());
      }

    } else {
      dispatch(setActiveStep(nextStep));
      setHighestStepVisited((hSC) => Math.max(hSC, nextStep));
    }
  };
  
  // Hacia atrás, libre (retroceder nunca valida, así se puede salir de un paso a medio llenar).
  // Hacia adelante, de a un paso: handleStepChange solo valida el paso que se está dejando, así
  // que saltar 2+ pasos en el stepper dejaba los del medio sin validar nunca — por ahí entraban
  // inscripciones con Datos Personales incompleto.
  const shouldAllowSelectStep = (step) =>
    highestStepVisited >= step && (step < activeStep || step === activeStep + 1);

  if(!knownUserLoaded && !knownUser.loading && queryParams.token){
    return (
      <Preloader loaderMessage='Estamos cargando su informacion ...' />
    )
  }

  // Gate del link cifrado: mientras se valida "l" contra NBAPI no se renderizan los pasos, para
  // que el precio nunca se calcule con el tipo de asistente por defecto.
  if (queryParams.l && linkEvento.loading) {
    return (
      <Preloader loaderMessage='Estamos validando su link de inscripción ...' />
    )
  }

  return (
    <div className='title' style={{
      backgroundColor: 'rgb(249, 250, 251)',
      overflowX:'hidden',
      width:'100%',
      }}
    >
      <div className='steps-section'>
        <div className='side-progress fixed'>
          <div className='side-progress-content'>
            <SideStepper
              steps={steps}
              activeStep={activeStep}
              onStepClick={(step) => handleStepChange(step)}
              isMobile={isMobile}
              shouldAllowSelectStep={shouldAllowSelectStep}
            />
          </div>
        </div>
        <div className={`form form-fixed`}>
          {knownUser.loading ? (
            <Center h={'100vh'}>
              <Loader color="#f20a61" size="lg" type="bars" />
            </Center>
          ):(
            <>
              <div className='program-section'>
                {/* Aviso de link de linksEventos inválido/vencido/sin cupos: dentro de la columna
                    del formulario (el panel lateral fijo tapaba el banner cuando iba arriba de todo). */}
                {queryParams.l && linkEvento.error && (
                  <div
                    role='alert'
                    style={{
                      backgroundColor: '#FFF4E5',
                      color: '#7A4F01',
                      border: '1px solid #F5C26B',
                      borderRadius: 8,
                      padding: '10px 16px',
                      marginBottom: 16,
                      width: '95%',
                      fontSize: 14,
                    }}
                  >
                    {linkEvento.error} — podés continuar tu inscripción con el arancel general.
                  </div>
                )}
                {/* Token de usuario conocido inválido o vencido (Redis TTL): sin este aviso el
                    aspirante seguía como externo sin enterarse y perdía el descuento comunidad. */}
                {queryParams.token && knownUser.error && !knownUser.comunity && (
                  <div
                    role='alert'
                    style={{
                      backgroundColor: '#FFF4E5',
                      color: '#7A4F01',
                      border: '1px solid #F5C26B',
                      borderRadius: 8,
                      padding: '10px 16px',
                      marginBottom: 16,
                      width: '95%',
                      fontSize: 14,
                    }}
                  >
                    El link de acceso venció o no es válido. Podés continuar completando tus datos en
                    "Datos Personales". Si sos parte de la comunidad UAI, al ingresar tu documento vas a
                    poder validar tu identidad para recuperar tus datos y beneficios.
                  </div>
                )}
                <Row className="g-3">
                  <Col md={12} lg={8}>
                    <div>
                      <Title order={2} fw={300} mb='xs'>
                        Solicitud de Preinscripción a <Text span inherit fw={500} c={"#f20a61"}> {typeConfig[queryParams.t].description} </Text>
                      </Title>
                      { !isMobile && (
                        <Text size='lg'>
                          Completa este formulario seleccionando la carrera, curso o diplomatura de tu interés. 
                          Revisa las fechas y opciones para encontrar la que mejor se alinee con tu plan.
                        </Text>
                      )}
                    </div>
                  </Col>
                  <Col md={12} lg={4}>
                    <Center h={'100%'}>
                      <Button
                        variant="filled"
                        color="#f20a61"
                        onClick= {handleBonusesButtonClick}
                        size='md'
                        fullWidth ={isMobile}
                      >
                        Ver bonificaciones
                      </Button>
                    </Center>
                  </Col>
                </Row>
              </div>
              {summary.precioOriginal != null && activeStep != 0 && activeStep != (steps.length - 1) && (
                <Paper 
                  shadow="md" 
                  radius="md" 
                  withBorder 
                  padding="xl" 
                  w='95%'
                  style={{ border: '2px solid rgb(242, 10, 95)'}}
                >
                  <div>
                    <Row className="py-3">
                    {queryParams.t !== "m" ? (
                    <>
                      <Col md={3} style={{ textAlign: 'center' }}>
                        <Text fw={600} c={'rgb(242, 10, 95)'}>
                          {typeConfig[queryParams.t]?.description}
                        </Text>
                        <Text size='lg' fw={500} c={'#333'}>
                          {summary.carrera}
                        </Text>
                      </Col>
                      <Col md={3} style={{ textAlign: 'center' }}>
                        <Text fw={600} c={'rgb(242, 10, 95)'}>Campus</Text>
                        <Text size='lg' fw={500} c={'#333'}>
                          {summary.campus}
                        </Text>
                      </Col>
                      <Col md={3} style={{ textAlign: 'center' }}>
                        <Text fw={600} c={'rgb(242, 10, 95)'}>Turno</Text>
                        <Text size='lg' fw={500} c={'#333'}>
                          {summary.turno}
                        </Text>
                      </Col>
                      <Col md={3} style={{ textAlign: 'center' }}>
                        <Text fw={600} c={'rgb(242, 10, 95)'}>
                          Valor de la matrícula
                        </Text>
                        <TextPriceWithDiscount
                          size='lg'
                          fontWeight={500}
                          price={summary.precioOriginal || 0}
                          discountedPrice={summary.precioDescuento || 0}
                          priceComunityUAI={summary.precioComunidad}
                          isComunidad={knownUser.comunity && summary.precioComunidad != null}
                        />
                      </Col>
                    </>
                  ) : (
                    <>
                      <Col md={3} style={{ textAlign: 'center' }}>
                        <Text fw={600} c={'rgb(242, 10, 95)'}>
                          {typeConfig[queryParams.t]?.description}
                        </Text>
                        <Text size='lg' fw={500} c={'#333'}>
                          {summary.carrera}
                        </Text>
                      </Col>
                      <Col md={3} style={{ textAlign: 'center' }}>
                        <Text fw={600} c={'rgb(242, 10, 95)'}>Campus</Text>
                        <Text size='lg' fw={500} c={'#333'}>
                          {summary.campus}
                        </Text>
                      </Col>
                      <Col md={3} style={{ textAlign: 'center' }}>
                        <Text fw={600} c={'rgb(242, 10, 95)'}>Turno</Text>
                        <Text size='lg' fw={500} c={'#333'}>
                          {summary.turno}
                        </Text>
                      </Col>
                      <Col md={3} style={{ textAlign: 'center' }}>
                        <Text fw={600} c={'rgb(242, 10, 95)'}>
                          Valor de la microcredencial
                        </Text>
                      
                        <TextPriceWithDiscount
                          size='lg'
                          fontWeight={500}
                          price={summary.precioOriginal || 0}
                          discountedPrice={summary.precioDescuento || 0}
                          priceComunityUAI={summary.precioComunidad}
                          isComunidad={knownUser.comunity && summary.precioComunidad != null}
                        />
                      </Col>
                    </>
                  )}
                    </Row>
                  </div>
                </Paper>
              )}
              <div className='form-header' ref={sectionTitleRef}>
                <Stack>
                  <Text ta={'center'} size="35px" weight={900} mb={10}>
                    {steps[activeStep]?.title}
                  </Text>
                  <Text ta={'center'} size="md" c="dimmed">
                    {steps[activeStep]?.description}
                  </Text>
                </Stack>
              </div>
              <div className='form-content' 
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                  style={{ marginBottom: '1rem', padding: '1rem' }}
                >
                    {finishStatus.finishInit  ? (
                      <>
                        <Container h={'40vh'}>
                          <Flex
                            h={'100%'}
                            gap="md"
                            justify="center"
                            align="center"
                            direction="column"
                            wrap="wrap"
                          >
                            {finishStatus.isLoading ? (
                              <Center>
                                <Stack align="center" justify="center" gap={'sm'}>
                                  <Loader color="pink" size="lg" type="dots" />
                                  <Text mt="sm" ta={'center'}>Estamos procesando su preinscripción</Text>
                                </Stack>
                              </Center>
                            ) : finishStatus.saveSuccess === false ? (
                              <Center>
                                <Stack align="center" justify="center" gap={'sm'}>
                                  <IconCircleDashedX size={50} color="red" />
                                  <Text mt="sm">Ocurrió un error, pronto sera redirigido al formulario ...</Text>
                                </Stack>
                              </Center>
                            ) : null}

                          </Flex>
                        </Container>
                      </>
                    ) : 
                      steps.find(step => step.number === activeStep + 1)?.form
                    }
                </motion.div>
                <div style={{
                  padding: '1rem 2rem',
                  borderTop: '2px solid rgba(242, 10, 95, 0.57)',
                }}>
                  <Group justify="flex-end" wrap='nowrap'>
                    {!finishStatus.finishInit && (
                      <>
                        {activeStep === 0 && (
                          <Button
                            variant="filled"
                            color="#f20a61"
                            fullWidth={isMobile}
                            onClick={() => handleStepChange(activeStep + 1)}
                          >
                            {isMobile ? "Continuar" : "Continuar con la preinscripción"}
                          </Button>
                        )}

                        {activeStep > 0 && activeStep < steps.length - 1 && (
                          <>
                            <Button
                              variant="filled"
                              color="#C1C1C1"
                              onClick={() => handleStepChange(activeStep - 1)}
                            >
                              {isMobile ? <IconArrowLeft /> : "Anterior"}
                            </Button>
                            <Button
                              variant="filled"
                              color="#f20a61"
                              fullWidth={isMobile}
                              onClick={() => handleStepChange(activeStep + 1)}
                            >
                              Siguiente
                            </Button>
                          </>
                        )}

                        {activeStep === steps.length - 1 && (
                          <Button
                            variant="filled"
                            color="#f20a61"
                            miw="30%"
                            fullWidth={isMobile}
                            disabled={
                              !ConfirmationResponse?.aceptaCondiciones || 
                              (queryParams.t === 'm' && !ConfirmationResponse?.aceptaRequerimientos)
                            }
                            onClick={() => handleStepChange(activeStep + 1)}
                          >
                            Confirmar Preinscripción
                          </Button>
                        )}
                      </>
                    )}
                  </Group>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <BonusesInfoModal
        t={queryParams.t}
        isOpened={bonusesOpened}
        onClose={() => setBonusesOpened(false)}
      />
    </div>
  );
};

export default CheckoutPage;